import { useCallback, useEffect, useRef, useState } from 'react';
import { APP_NAME } from '../../../config/branding';
import { MESSAGING_UNREAD_CHANGED_EVENT } from './messagingEvents';
import { PROFILE_UPDATED_EVENT } from '../profilePictureUrl';
import { getStoredActiveProfileMode } from '../../profileIdentity/activeProfileMode';

const baseUrlFallback = process.env.REACT_APP_BASE_URL || '';
const POLL_MS = 30000;
const TITLE_BADGE_RE = /^\(\d+\)\s+[^·]+·\s+/;

function formatUnreadTitleLabel(count, senders = [], latestPreview = '') {
  if (!count) return '';
  let who = 'Messages';
  if (senders.length === 1) who = senders[0];
  else if (senders.length === 2) who = `${senders[0]}, ${senders[1]}`;
  else if (senders.length > 2) who = `${senders[0]} +${senders.length - 1}`;
  if (latestPreview && senders.length === 1) {
    return `(${count}) ${who}: ${latestPreview}`;
  }
  return `(${count}) ${who}`;
}

function stripMessagingTitleBadge(title) {
  return String(title || '').replace(TITLE_BADGE_RE, '');
}

function applyMessagingTitleBadge({ count, senders, latestPreview }) {
  if (typeof document === 'undefined') return;
  const baseTitle = stripMessagingTitleBadge(document.title) || APP_NAME;
  if (!count) {
    if (document.title !== baseTitle) document.title = baseTitle;
    return;
  }
  const label = formatUnreadTitleLabel(count, senders, latestPreview);
  const next = `${label} · ${baseTitle}`;
  if (document.title !== next) document.title = next;
}

export function useMessagingUnreadCount(baseUrl = baseUrlFallback) {
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadSenders, setUnreadSenders] = useState([]);
  const [latestPreview, setLatestPreview] = useState('');
  const baseTitleRef = useRef(typeof document !== 'undefined' ? document.title : APP_NAME);

  const refreshUnreadCount = useCallback(async () => {
    try {
      const res = await fetch(`${baseUrl}/api/messaging/unread-count`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ includeSummary: true, viewerProfileMode: getStoredActiveProfileMode() }),
      });
      const data = await res.json().catch(() => ({}));
      if (data?.success) {
        const count = Number(data.count) || 0;
        setUnreadCount(count);
        setUnreadSenders(Array.isArray(data.senders) ? data.senders : []);
        setLatestPreview(String(data.latestPreview || ''));
        applyMessagingTitleBadge({
          count,
          senders: Array.isArray(data.senders) ? data.senders : [],
          latestPreview: String(data.latestPreview || ''),
        });
      }
    } catch {
      /* ignore */
    }
  }, [baseUrl]);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      baseTitleRef.current = stripMessagingTitleBadge(document.title) || APP_NAME;
    }
    refreshUnreadCount();
  }, [refreshUnreadCount]);

  useEffect(() => {
    const onFocus = () => refreshUnreadCount();
    const onUnreadChanged = () => refreshUnreadCount();
    const onProfileUpdated = () => refreshUnreadCount();
    window.addEventListener('focus', onFocus);
    window.addEventListener(MESSAGING_UNREAD_CHANGED_EVENT, onUnreadChanged);
    window.addEventListener(PROFILE_UPDATED_EVENT, onProfileUpdated);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener(MESSAGING_UNREAD_CHANGED_EVENT, onUnreadChanged);
      window.removeEventListener(PROFILE_UPDATED_EVENT, onProfileUpdated);
    };
  }, [refreshUnreadCount]);

  useEffect(() => {
    const tick = () => {
      if (document.visibilityState === 'visible') refreshUnreadCount();
    };
    const id = window.setInterval(tick, POLL_MS);
    return () => window.clearInterval(id);
  }, [refreshUnreadCount]);

  return {
    unreadCount,
    unreadSenders,
    latestPreview,
    hasUnread: unreadCount > 0,
    refreshUnreadCount,
  };
}
